/**
 * Dashboard widget showing the admin's up-to-3 pinned saved filters with
 * live match counts. Clicking a pin deep-links to the relevant admin tab
 * with the saved filter pre-applied via sessionStorage.
 *
 * Auto-refreshes the live counts every 60s.
 */
import { useEffect, useState, useCallback } from "react";
import API from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Pin, ArrowRight, Loader2 } from "lucide-react";

// scope → AdminDashboard activeTab key (subtab keys are used directly as activeTab).
const SCOPE_ROUTING = {
  orders:    { tab: "orders",        label: "Orders",          tone: "bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-800" },
  users:     { tab: "users",         label: "Users",           tone: "bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-800" },
  audit:     { tab: "audit",         label: "Audit Logs",      tone: "bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700" },
  errors:    { tab: "errors",        label: "Error Reports",   tone: "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-800" },
  coupons:   { tab: "loyalty",       label: "Coupons",         tone: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800" },
  referrals: { tab: "loyalty",       label: "Referrals",       tone: "bg-pink-50 text-pink-700 border-pink-200 dark:bg-pink-900/30 dark:text-pink-300 dark:border-pink-800" },
  tracking:  { tab: "tracking",      label: "Tracking Queue",  tone: "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800" },
  webhooks:  { tab: "lifefile-kpis", label: "Webhooks",        tone: "bg-cyan-50 text-cyan-700 border-cyan-200 dark:bg-cyan-900/30 dark:text-cyan-300 dark:border-cyan-800" },
  documents: { tab: "pharmacy", label: "Pharmacy Docs",   tone: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800" },
};

export default function PinnedFiltersWidget({ onJump }) {
  const [pinned, setPinned] = useState([]);
  const [max, setMax] = useState(3);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await API.get("/admin/saved-filters/pinned");
      setPinned(data.pinned || []);
      if (typeof data.max === "number") setMax(data.max);
    } catch {
      /* widget is non-critical — fail quietly */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    // Refresh live counts every 60s so the "23 matches" number stays current
    // without burning bandwidth.
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const handleJump = (f) => {
    const routing = SCOPE_ROUTING[f.scope];
    if (!routing) return;
    // Stash the payload so the destination tab's AdminSearchPanel picks it
    // up on mount. Keyed by scope so different scopes don't collide.
    try {
      sessionStorage.setItem(`pinned_filter_apply:${f.scope}`, JSON.stringify(f.payload || {}));
    } catch { /* private mode etc — non-fatal */ }
    if (onJump) onJump(routing.tab);
  };

  if (loading) {
    return (
      <Card className="mb-6">
        <CardContent className="p-5 flex items-center gap-2 text-slate-400 dark:text-slate-500 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading your pinned filters…
        </CardContent>
      </Card>
    );
  }

  if (pinned.length === 0) {
    return (
      <Card className="mb-6 border-dashed" data-testid="pinned-filters-empty">
        <CardContent className="p-5 flex items-center gap-3 text-sm text-slate-500 dark:text-slate-400">
          <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
            <Pin className="w-4 h-4 text-slate-400 dark:text-slate-500" />
          </div>
          <div>
            <p className="font-medium text-slate-700 dark:text-slate-200">No pinned filters yet</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              Save a search on any admin tab, then pin up to {max} so they land here with live counts.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="mb-6" data-testid="pinned-filters-widget">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-1.5">
          <Pin className="w-4 h-4 text-purple-600" /> Your pinned searches
        </h3>
        <span className="text-xs text-slate-400 dark:text-slate-500">{pinned.length} of {max}</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {pinned.map((f) => {
          const routing = SCOPE_ROUTING[f.scope] || { label: f.scope, tone: "bg-slate-50 text-slate-700 border-slate-200" };
          const hasCount = typeof f.live_count === "number" && f.live_count >= 0;
          return (
            <button
              type="button"
              key={f.id}
              onClick={() => handleJump(f)}
              className={`group text-left rounded-2xl border p-4 hover:shadow-md hover:-translate-y-0.5 transition-all ${routing.tone}`}
              data-testid={`pinned-filter-${f.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <span className="text-[10px] font-semibold uppercase tracking-wider opacity-75">
                  {routing.label}
                </span>
                <ArrowRight className="w-4 h-4 opacity-50 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
              </div>
              <p className="mt-2 text-sm font-semibold leading-snug line-clamp-2">{f.name}</p>
              <div className="mt-3 flex items-baseline gap-2">
                {hasCount ? (
                  <>
                    <span className="text-3xl font-bold leading-none">{f.live_count.toLocaleString()}</span>
                    <span className="text-[11px] opacity-75">{f.live_count === 1 ? "match" : "matches"}</span>
                  </>
                ) : (
                  <span className="text-xs opacity-60">live count unavailable</span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
